import * as React from 'react';
import { useNavigate as _useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import { useQueryClient } from '@tanstack/react-query';
import MarcoDrawer from '../Drawer';
import MobileMenuButton from '../MobileMenuButton';
import { useUserLocation as _useUserLocation } from '../../hooks/useUserLocation';
import { useAuth } from '../../hooks';
import {
  useAppDispatch,
  logout,
  useAppSelector,
  setAdminTakeoverCompanyUser,
  setShowSidebar,
} from '../../store';
import { ADMIN_TAKEOVER_TOKEN_STORAGE_KEY, CURRENT_USER } from '../../constants/auth';
import { resetStore } from '../../utils/helpers';

type LayoutProps = {
  children: React.ReactNode;
  useNavigate?: Function;
  useUserLocation?: Function;
  useLocation?: Function;
};

const Layout: React.FC<LayoutProps> = ({
  children,
  useUserLocation = _useUserLocation,
  useNavigate = _useNavigate,
}) => {
  const dispatch = useAppDispatch();
  const { adminTakeoverCompanyUser, showSidebar } = useAppSelector((state) => state.auth);
  const navigate = useNavigate();
  const { isAdmin, isLender, user } = useAuth();
  const queryClient = useQueryClient();

  const setLogout = () => {
    queryClient.clear();
    if (adminTakeoverCompanyUser && !!localStorage.getItem(ADMIN_TAKEOVER_TOKEN_STORAGE_KEY)) {
      localStorage.removeItem(ADMIN_TAKEOVER_TOKEN_STORAGE_KEY);
      localStorage.removeItem(CURRENT_USER);
      const companyUser = adminTakeoverCompanyUser;
      dispatch(setAdminTakeoverCompanyUser(null));
      window.location.replace(`/companies/${companyUser}`);
    } else {
      dispatch(logout())
        .unwrap()
        .then(() => {
          navigate('/login');
          resetStore(dispatch);
        });
    }
  };

  const handleShowSidebar = (show: boolean) => {
    dispatch(setShowSidebar(show));
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <MobileMenuButton onClick={() => handleShowSidebar(true)} isAdmin={isAdmin} />
      <MarcoDrawer
        open={showSidebar}
        setOpen={handleShowSidebar}
        isAdmin={isAdmin}
        isLender={isLender}
        user={user}
        logoutUser={setLogout}
      />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, md: 4 },
          backgroundColor: '#F1F6FD',
          minHeight: '100vh',
          // Add top margin on mobile to accommodate the header area
          mt: { xs: '64px', md: 0 },
        }}
      >
        <Container
          sx={{
            maxWidth: { xs: '100%', lg: 1272, xl: 1500 },
            paddingBottom: '1rem',
          }}
        >
          {children}
        </Container>
      </Box>
    </Box>
  );
};

export default Layout;
